import {
  Component,
  contentChildren,
  inject,
  input,
  output,
  ElementRef,
  effect,
  AfterViewInit,
  NgZone,
  OnDestroy,
  DOCUMENT
} from '@angular/core';
import {NOTIFICATION_ITEM_TOKEN} from '../notification-item.base';

import {EmptyStateComponent} from '../../../core/ui/elements/components/empty-state/empty-state.component';

/**
 * NotificationPanelComponent provides the UI for displaying notification items.
 * It ensures that the panel is always properly placed within the viewport,
 * adjusting its position when it cannot be fully contained when visible.
 */
@Component({
  selector: 'app-notification-panel',
  imports: [EmptyStateComponent],
  templateUrl: './notification-panel.component.html',
  styleUrl: './notification-panel.component.scss',
  host: {
    '[class.visible]': 'visible()',
    '[style.display]': 'visible() ? "block" : "none"'
  }
})
export class NotificationPanelComponent implements AfterViewInit, OnDestroy {
  /** Collection of notification items projected into this component */
  items = contentChildren(NOTIFICATION_ITEM_TOKEN);
  itemsCount = input(0);

  /**
   * Checks if there are notification items to display
   * This method can be overridden in tests
   */
  hasItems(): boolean {
    return this.itemsCount() > 0;
  }

  /** Document reference for DOM operations */
  private readonly document = inject(DOCUMENT);

  /** Signal input to control the visibility of the panel */
  visible = input(false);

  /** Event emitted when the "Clear all" button is clicked */
  clearAll = output<void>();

  /** Reference to the component's host element */
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  /** NgZone service for running code outside Angular's change detection */
  private readonly ngZone = inject(NgZone);

  /** Window reference for viewport dimensions */
  private readonly window = this.document.defaultView;

  /** Timeout ID for position updates */
  private positionUpdateTimeoutId: number | null = null;

  constructor() {
    // Use effect to watch for changes to the visible input
    effect(() => {
      if (this.visible()) {
        this.schedulePositionUpdate();
      }
    });
  }

  /**
   * After view initialization lifecycle hook.
   * Updates the panel position if it's visible.
   */
  ngAfterViewInit(): void {
    if (this.visible()) {
      this.updatePosition();
    }
  }

  /**
   * Cleanup when component is destroyed
   */
  ngOnDestroy(): void {
    this.clearPositionUpdateTimeout();
  }

  /**
   * Schedules a position update to run outside Angular's change detection
   * to improve performance.
   */
  private schedulePositionUpdate(): void {
    this.clearPositionUpdateTimeout();

    this.ngZone.runOutsideAngular(() => {
      // Use setTimeout to ensure the panel is rendered before calculating position
      this.positionUpdateTimeoutId = window.setTimeout(() => this.updatePosition(), 0);
    });
  }

  /**
   * Clears any pending position update timeout
   */
  private clearPositionUpdateTimeout(): void {
    if (this.positionUpdateTimeoutId !== null) {
      window.clearTimeout(this.positionUpdateTimeoutId);
      this.positionUpdateTimeoutId = null;
    }
  }

  /**
   * Updates the position of the panel to ensure it stays within the viewport.
   * This method adjusts the panel's position if it would overflow the viewport.
   */
  updatePosition(): void {
    if (!this.visible() || !this.window) {
      return;
    }

    const panelElement = this.elementRef.nativeElement;
    const rect = panelElement.getBoundingClientRect();

    // Get viewport dimensions
    const viewportWidth = this.window.innerWidth;
    const viewportHeight = this.window.innerHeight;

    this.adjustHorizontalPosition(panelElement, rect, viewportWidth);
    this.adjustVerticalPosition(panelElement, rect, viewportHeight);
  }

  /**
   * Adjusts the horizontal position of the panel to prevent overflow
   */
  private adjustHorizontalPosition(
    element: HTMLElement,
    rect: DOMRect,
    viewportWidth: number
  ): void {
    const overflowRight = rect.right > viewportWidth;

    if (overflowRight) {
      const newLeft = viewportWidth - rect.width;
      element.style.left = `${Math.max(0, newLeft)}px`;
    }
  }

  /**
   * Adjusts the vertical position of the panel to prevent overflow
   */
  private adjustVerticalPosition(
    element: HTMLElement,
    rect: DOMRect,
    viewportHeight: number
  ): void {
    const overflowBottom = rect.bottom > viewportHeight;

    if (overflowBottom) {
      const newTop = viewportHeight - rect.height;
      element.style.top = `${Math.max(0, newTop)}px`;
    }
  }
}
